/* eslint-disable */
import type { MovementReason } from "../banklin/movement_reason.pb";
import type { OctopusResult } from "../banklin/octopus.pb";
import type { RecipientBalanceDenialReason } from "../cash/p2p/RecipientBalanceDenialReasonProto.pb";
import type { Money } from "../common/currency.pb";
import type { GeoLocation } from "../common/location.pb";
import type { Headers } from "../common/protocols.pb";
import type { AuthorizationError } from "../payments/auth.pb";
import type { Account } from "./accounts.pb";
import type { Alias } from "./alias.pb";
import type { BankAccount } from "./bank_account.pb";
import type { BrowserInteraction } from "./browser_interaction.pb";
import type { Customer } from "./customer.pb";
import type { DepositPreference } from "./deposit_preference.pb";
import type { InstrumentLink } from "./instrument_link.pb";
import type { Orientation } from "./orientation.pb";
import type { PaymentCard } from "./payment_card.pb";
import type { PaymentPullState } from "./payment_pull_state.pb";
import type { PaymentPushState } from "./payment_push_state.pb";
import type { RatePlan } from "./rate_plan.pb";
import type { ThreeDomainSecureAuthentication } from "./sca/three_domain_secure.pb";
import type { SignalsContext } from "./signal.pb";
import type { StoredValue } from "./stored_value.pb";

/** Whether funds are coming in or going out. */
export enum Direction {
  /** PULL - Move money from a customer to Square. */
  PULL = "PULL",
  /** PUSH - Move money from Square to a customer. */
  PUSH = "PUSH",
}

export enum CrossBorderPaymentType {
  CROSS_BORDER = "CROSS_BORDER",
  DOMESTIC = "DOMESTIC",
  /** INCONCLUSIVE - It could not be determined if it is cross border yet. * */
  INCONCLUSIVE = "INCONCLUSIVE",
}

/** Represents a Franklin payment. */
export interface Payment {
  id?: string;
  external_id?: string;
  sender?: Customer;
  recipient?: Customer;
  orientation?: Orientation;
  amount?: Money;
  pull_amount?: Money;
  push_amount?: Money;
  deposit_preference?: DepositPreference;
  /** @deprecated */
  amount_with_fee?: Money;
  /** @deprecated */
  source_email_headers?: Headers;
  state?: PaymentState;
  failure_reason?: PaymentFailureReason;
  created_at?: number;
  /**
   * Time when the client attempted to make a request in offline mode. Provided in the RetryContext
   * of retried payment requests.
   */
  client_created_at?: number;
  updated_at?: number;
  /** @deprecated */
  edje_decline_reason?: AuthorizationError;
  last_recipient_reminder_sent_at?: number;
  /** @deprecated */
  email_body?: string;
  creation_mechanism?: PaymentCreationMechanism;
  pack_size?: number;
  pack_index?: number;
  /** The schedule associated to this payment, and the planned send date for this payment. */
  payment_schedule_token?: string;
  send_at?: number;
  /**
   * Deprecated and no longer populated.
   *
   * @deprecated
   */
  creation_user_agent?: string;
  /**
   * These fields are deprecated and no longer populated.
   *
   * @deprecated
   */
  dispute_reason_code?: string;
  /** @deprecated */
  dispute_user_comments?: string;
  /**
   * These deprecated fields are obsoleted by the 'pull' and 'push' messages.
   * Prefer those fields because they're populated even if the pull or push is
   * incomplete or unsuccessful.
   *
   * @deprecated
   */
  sender_transaction_id?: string;
  /** @deprecated */
  sender_card?: PaymentCard;
  /** @deprecated */
  recipient_card?: PaymentCard;
  /** @deprecated */
  recipient_bank_account?: BankAccount;
  /** @deprecated */
  edje_authorization_result_reason?: string;
  /** @deprecated */
  recipient_transaction_id?: string;
  /** @deprecated */
  sender_charged_at?: number;
  /** @deprecated */
  recipient_paid_at?: number;
  /** @deprecated */
  edje_gateway_id?: string;
  /** @deprecated */
  capture_mechanism?: PaymentMoneyMovingMechanism;
  /** @deprecated */
  deposit_mechanism?: PaymentMoneyMovingMechanism;
  pull?: PaymentPull[];
  push?: PaymentPush[];
  refund_pulls?: PaymentPull[];
  refund_pushes?: PaymentPush[];
  reclaim_pulls?: PaymentPull[];
  is_refund_to_balance?: boolean;
  /**
   * Deprecated and no longer populated.
   *
   * @deprecated
   */
  sender_opaque_app_token?: string;
  /**
   * Deprecated and no longer populated.
   *
   * @deprecated
   */
  sender_device_uuid?: string;
  /**
   * Deprecated and no longer populated.
   *
   * @deprecated
   */
  incoming_email?: IncomingEmail;
  /**
   * Deprecated and no longer populated.
   *
   * @deprecated
   */
  geo_location?: GeoLocation;
  /**
   * True if, at the time of the payment, the customer had passcode confirmation enabled.
   * If the payment is successful, it means that the customer entered the right passcode.
   */
  requires_passcode_confirmation?: boolean;
  /**
   * Always null.
   *
   * @deprecated
   */
  referrer_id?: string;
  /**
   * The original sender id. It will match the sender's id unless this payment has been transferred
   * to a new customer during a customer merge.
   */
  original_sender_id?: string;
  /**
   * The original sender token. It will match the sender's token unless this payment has been
   * transferred to a new customer during a customer merge.
   */
  original_sender_token?: string;
  /**
   * The original recipient id. It will match the recipient's id unless this payment has been
   * transferred to a new customer during a customer merge.
   */
  original_recipient_id?: string;
  /**
   * The original recipient token. It will match the recipient's token unless this payment has been
   * transferred to a new customer during a customer merge.
   */
  original_recipient_token?: string;
  sender_alias?: Alias;
  recipient_alias?: Alias;
  initiator_notes?: string;
  /**
   * Client-specified external non-unique data to associated to this payment.
   * Data is limited to 64 characters.
   */
  custom_id?: string;
  /** The ID of the client that created this payment. */
  client_id?: string;
  /** The initiator's browser interaction with email headers or HTTP headers. */
  creating_browser_interaction?: BrowserInteraction;
  rate_plan?: RatePlan;
  /** Contains the reason that the attempt to refund this payment failed. */
  refund_failure_reason?: PaymentRefundFailureReason;
  /** Date when the payment became visible to the sender, in milliseconds since epoch. */
  reached_sender_at?: number;
  /** Date when the payment became visible to the recipient, in milliseconds since epoch. */
  reached_recipient_at?: number;
  /** Date when the payment's refund became visible to the sender, in milliseconds since epoch. */
  refund_reached_sender_at?: number;
  /**
   * An 8-character token that uniquely represents the payment. In the UI, its the transaction ID.
   * e.g. Transaction #unp2ka6r
   */
  display_id?: string;
  /** The reason why the recipient wasn't allowed to deposit this payment into their balance. */
  recipient_balance_denial_reason?: RecipientBalanceDenialReason;
  /** Sender's account that the payment was sent from. */
  sender_account?: Account;
  /** Recipient's account that the payment was sent to. */
  recipient_account?: Account;
  /** The pinned instruments used for the pull and push. */
  pull_instrument?: InstrumentLink;
  push_instrument?: InstrumentLink;
  /** To support the transfer rollout, risk systems evaluate transfers as payments. */
  is_transfer?: boolean;
  state_transition_count?: number;
  /**
   * Version of the sender data within this payment. When receiving multiple updates of the same
   * payment, use this to differentiate which snapshot has the newest sender information. This may
   * be different than the most recently received version because of feed sharding.
   */
  sender_version?: number;
  /** Version of the recipient data within this payment. */
  recipient_version?: number;
  /**
   * Version metadata for fields shared by the sender and recipient of the payment. Noteworthy
   * shared fields are the payment's state, failure reason, failed at date, rate plan. Most fields
   * are not shared; see DbMovement for the full set.
   */
  twin_lock_version?: number;
  sender_reported_abuse_at?: number;
  recipient_reported_abuse_at?: number;
  estimated_arrival_at?: number;
  forced_instant_deposit_with_fee_at?: number;
  /** To mark if a payment is cross-border. */
  cross_border?: boolean;
  sender_payment_amount_in_default_currency?: Money;
  recipient_payment_amount_in_default_currency?: Money;
  movement_exchange_rate?: string;
  /**
   * Information sent from the client in the context of a request made to the Franklin.
   * For example, this message contains the touch events collected by the client
   * to be evaluated by Frisky on every payment request.
   */
  signals_context?: SignalsContext;
  refund_requested_at?: number;
  refund_request_reason?: string;
  refund_request_declined_at?: number;
  /**
   * List of all state transitions, in chronological order, used for risk processing.
   * This is only populated when we publish Payment events.
   */
  state_transitions?: PaymentStateTransition[];
  /** Shows the migration step of the money-mover service roll out. */
  money_mover_migration_step?: string;
  /** 3DS authentication data. It should only be populated for consumption by Risk data pipelines. */
  three_domain_secure_authentication?: ThreeDomainSecureAuthentication;
  /** A URN representing the initiator's browser interaction. */
  creating_browser_interaction_token?: string;
}

/**
 * Creation mechanisms. Even if the email is created via the in-app integration, the value
 * will still be just EMAIL.
 */
export enum PaymentCreationMechanism {
  /**
   * EMAIL - Payments received initially as email messages to cash@square.com or
   * request@square.com. Includes payments initiated by the 1.x app.
   */
  EMAIL = "EMAIL",
  /** RPC - Legacy payments created via an RPC endpoint that we've since shut down. */
  RPC = "RPC",
  /**
   * TPP - Payments created via our third-party platform. In order to specialize
   * business logic, some platform-initiated payments may be given a more
   * specific creation mechanism.
   */
  TPP = "TPP",
  /**
   * APP - Payments created via the native payment-sending app flow. Doesn't include
   * email payments created by the 1.x app.
   */
  APP = "APP",
  /**
   * TRY_IT_NOW - Payments created through the try-it-now flow on the Cash web site.
   * Obsolete; replaced with WELCOME_BONUS.
   */
  TRY_IT_NOW = "TRY_IT_NOW",
  /** REFERRAL_BOUNTY - Payments paid to referrers for signing up their friends. */
  REFERRAL_BOUNTY = "REFERRAL_BOUNTY",
  /** BUSINESS_REFERRAL_BOUNTY - Payments paid to referrers for signing up a business. */
  BUSINESS_REFERRAL_BOUNTY = "BUSINESS_REFERRAL_BOUNTY",
  /** WELCOME_BONUS - Payments paid to customers that complete app enrollment. */
  WELCOME_BONUS = "WELCOME_BONUS",
  /** REWARD - Payments to customers for completing an app payment. */
  REWARD = "REWARD",
  /** WEB_SIGNUP - Deprecated. Obsoleted by `WEB`. */
  WEB_SIGNUP = "WEB_SIGNUP",
  /** WEB - Payments created on the web on a Cashtag owner's profile page. */
  WEB = "WEB",
  /** CASHBOARD - Payments created on the web in the Cashboard. */
  CASHBOARD = "CASHBOARD",
  /** RECURRING_PAYMENTS - Payments initiated by our recurring payments rule. */
  RECURRING_PAYMENTS = "RECURRING_PAYMENTS",
  /** REIMBURSEMENT - Payments paid to customers to refund fees or award missing referrals, etc. */
  REIMBURSEMENT = "REIMBURSEMENT",
  /** BANKSY - Payments use to transfer fund between Cash and Banksy. */
  BANKSY = "BANKSY",
  /**
   * CLAIMABLE - A payment where the getter is unknown at creation but will eventually be claimed by a Cash
   * App customer.
   *
   * Note that IMESSAGE, PAY_WITH_CASH, and others are also considered "claimable" payments.
   *
   * This was added as part of the Hack Week 2019 "Pay With Cash" project:
   *   https://docs.google.com/document/d/1vHwTb9xJDbdgIh2plvhDIELyIu9REjzptN1zyisj1_Q/edit?usp=sharing
   * It also builds off an earlier Hack Week that added QR code support (mdruker@).
   */
  CLAIMABLE = "CLAIMABLE",
  /** IMESSAGE - A CLAIMABLE payment initiated by the iOS app through iMessage. */
  IMESSAGE = "IMESSAGE",
  /**
   * PAY_WITH_CASH - A CLAIMABLE payment initiated by a merchant through the Pay With Cash API.
   *
   * This was added as part of the Hack Week 2019 "Pay With Cash" project:
   *   https://docs.google.com/document/d/1vHwTb9xJDbdgIh2plvhDIELyIu9REjzptN1zyisj1_Q/edit?usp=sharing
   */
  PAY_WITH_CASH = "PAY_WITH_CASH",
  /** ISSUED_CARD - Pseudo-payments that represent Issued Card purchases. */
  ISSUED_CARD = "ISSUED_CARD",
  /** CASH_ADVANCE - Cash advance payments from Cash App to customer. Unused since 2017-01. */
  CASH_ADVANCE = "CASH_ADVANCE",
  /** DDA_ACH - Payments initiated by Automated Clearing House. */
  DDA_ACH = "DDA_ACH",
  /**
   * CLAWBACK - Payments initiated in response to a payment or transfer chargeback,
   * in order to make Square whole.
   */
  CLAWBACK = "CLAWBACK",
  /**
   * P2P_BANKLIN - A generic peer-to-peer initiated payment.
   * Used in the Banklin money movement feed as there is no way for Banklin to know how the
   * payment was initiated from its point in the stack.
   */
  P2P_BANKLIN = "P2P_BANKLIN",
  /** DISCOUNT - A discount paid to customers for completing qualifying card purchases. */
  DISCOUNT = "DISCOUNT",
  /** PURCHASE - An account that contains money earned from purchase, e.g. money earned from change card design */
  PURCHASE = "PURCHASE",
  /** PAYROLL - Platform clients. */
  PAYROLL = "PAYROLL",
  YELLOW = "YELLOW",
  KEY_BANK = "KEY_BANK",
  ROBINHOOD = "ROBINHOOD",
  BUTTERCOIN = "BUTTERCOIN",
  LEDGER = "LEDGER",
  THX = "THX",
  CAVIAR = "CAVIAR",
  CAVIAR_RESTAURANT = "CAVIAR_RESTAURANT",
  CAVIAR_COURIER = "CAVIAR_COURIER",
  CAVIAR_DINER = "CAVIAR_DINER",
  SQCAPITAL = "SQCAPITAL",
  SQPAYROLL = "SQPAYROLL",
  SQPAYROLL_TO_EMPLOYER = "SQPAYROLL_TO_EMPLOYER",
  SQPAYROLL_TO_EMPLOYEE = "SQPAYROLL_TO_EMPLOYEE",
  /** CASHBOT - jwilson's hackweek project, uses Cash through Slack */
  CASHBOT = "CASHBOT",
  /**
   * PAYMENT_SCHEDULER - A payment created through an internal RPC by the Payment-Scheduler service,
   * for sending recurring payments.
   */
  PAYMENT_SCHEDULER = "PAYMENT_SCHEDULER",
  /** INSTRUMENT_VERIFICATION - A payment created through an internal RPC by the instrument verification manager. */
  INSTRUMENT_VERIFICATION = "INSTRUMENT_VERIFICATION",
  /** QR_CODE - A payment initiated through a QR code. */
  QR_CODE = "QR_CODE",
  /** NFC_TAG - A payment initiated through an NFC-tag */
  NFC_TAG = "NFC_TAG",
  /** COLLEGE_BONUS - Bonus payments for verified college students after signing up, ordering card, or using boost. */
  COLLEGE_BONUS = "COLLEGE_BONUS",
  /** INVEST_TRADE_ERROR - Invest Trade Error Payments. */
  INVEST_TRADE_ERROR = "INVEST_TRADE_ERROR",
  /** MARKETING - Payout for marketing. */
  MARKETING = "MARKETING",
  /** STOCK_PURCHASE - STOCK_PURCHASE should only be used to evaluate DbTransfer in risk during WaitingOnRiskStep. */
  STOCK_PURCHASE = "STOCK_PURCHASE",
  /** ASYNC_CASH_OUT - A cross border asynchronous cash out triggered for customers that expect auto cash out. */
  ASYNC_CASH_OUT = "ASYNC_CASH_OUT",
  /** PLAID_UPSELL_BONUS - Bonus payment for linking with plaid on the Plaid Upsell Bonus blocker. */
  PLAID_UPSELL_BONUS = "PLAID_UPSELL_BONUS",
  /** LOAN_PAYMENT - Loan payment for money transfer to stored balance. */
  LOAN_PAYMENT = "LOAN_PAYMENT",
  /** CROSS_BORDER_CASH_IN - A cross border cash in for customers that do not have enough funds to complete a cross-border payment. */
  CROSS_BORDER_CASH_IN = "CROSS_BORDER_CASH_IN",
  /** BITCOIN_PURCHASE - BITCOIN_PURCHASE should only be used to evaluate DbTransfer in risk during WaitingOnRiskStep. */
  BITCOIN_PURCHASE = "BITCOIN_PURCHASE",
  /** CHECK - Payment from deposit a check. */
  CHECK = "CHECK",
  /** SUPPORT_BALANCE_TRANSFER - Stored balance transfer, used by support to help customers recover orphaned balances. */
  SUPPORT_BALANCE_TRANSFER = "SUPPORT_BALANCE_TRANSFER",
  /** INVEST_P2P - Money Flow created by Invest-P2P service, see go/moneymover-invest-p2p. */
  INVEST_P2P = "INVEST_P2P",
  /** MONEY_MOVER_TEST - Money Flow created by Money Mover service for test purposes, see go/moneymover. */
  MONEY_MOVER_TEST = "MONEY_MOVER_TEST",
  /** PHYSICAL_CASH - Payment from deposit physical cash. */
  PHYSICAL_CASH = "PHYSICAL_CASH",
  /** BNPL - Buy Now Pay Later payment through a Pay with Cash integration. */
  BNPL = "BNPL",
  /** P2P_CORE - Money Flow created by p2p-core service, see go/p2p-assets-atlas */
  P2P_CORE = "P2P_CORE",
  /** INSTANT_PAYCHECK - A transaction performed for Instant Paycheck */
  INSTANT_PAYCHECK = "INSTANT_PAYCHECK",
  /** MONETA - Money Flow created by Moneta service, see go/moneta. */
  MONETA = "MONETA",
  /** WIRE_TRANSFER - Payment from a wire transfer. */
  WIRE_TRANSFER = "WIRE_TRANSFER",
  /** LIGHTNING_TRANSFER - A deposit or withdrawal via the Bitcoin Lightning network */
  LIGHTNING_TRANSFER = "LIGHTNING_TRANSFER",
  /** CASH_ME_OUTSIDE - An international remittance created by cash-me-outside with a 3rd party partner service */
  CASH_ME_OUTSIDE = "CASH_ME_OUTSIDE",
  /**
   * CASH_OUT - A transfer from customers stored balance to their external instrument. This goes through the
   * cash out service. See more at go/cashout.
   */
  CASH_OUT = "CASH_OUT",
  /** MONETA_P2P_INFLOW - A cash in from customers external instrument to their p2p stored balance via moneta. */
  MONETA_P2P_INFLOW = "MONETA_P2P_INFLOW",
  /** P2P_ENGINE - Payment Flow created by p2p-engine-service, see go/p2pengine */
  P2P_ENGINE = "P2P_ENGINE",
  /** ALLOWANCE - An automatic payment from a sponsor to a dependent */
  ALLOWANCE = "ALLOWANCE",
  /** OVERDRAFTLY - A transaction performed for OVERDRAFT */
  OVERDRAFTLY = "OVERDRAFTLY",
  /** CASH_FOR_BUSINESS - A Cash for Business non-P2P payment, such as Tap to Pay. */
  CASH_FOR_BUSINESS = "CASH_FOR_BUSINESS",
}

/**
 * cash@
 *
 *        VALIDATING ----------------------------->--------------------------+
 *            |                                                              |
 *            V                                                              V
 *        SCHEDULED ----------------------------->---------------------------+
 *            |                                                              |
 *            V                                                              V
 *    +-> WAITING ON SENDER ---------------------->--------------------------+
 *    |       |                                                              |
 *    |       V                                                              V
 *    +-- CAPTURING ------------------------------>--------------------------+
 *            |                                                              |
 *            V                                                              V
 *    +-> WAITING ON RECIPIENT ------------------->--------------------------+
 *    |       |                                                              |
 *    |       V                                                              V
 *    +<- WAITING ON RISK ------------------------>--------------------------+
 *    |       |                                                              |
 *    |       V                                                              V
 *    ^   WAITING TO PAYOUT ---------------------->--------------------------+
 *    |       |                                                              |
 *    |       V                                                              V
 *    +<-- PAYING -------------------------------->--------------------------+
 *    |      OUT  <--------------------------------------------+             |
 *    ^       |                                                |             |
 *    |       +--<-----------------+--<-----------+--<------+  |             |
 *    |       |                    ^              ^          \ |             |
 *    |       V                    |              |           \|             V
 *    +- RECLAIMING CAPTURE     WAITING ON --> PULLING --> WAITING TO <-> REFUNDING -> RECLAIMING
 *    |       ^         \        REFUNDER       REFUND        REFUND         ^         REFUND PUSH
 *    ^       |          \         ^                                         |             ^
 *    |       V           \        |                                         |             |
 *    |  ************     V        |                                         |             V
 *    +- * PAID OUT * ----+------>-+--------------------------------------->-+           FAILED
 *       ************
 *
 * bill@
 *
 *        VALIDATING ----------------------------->--------------------------+
 *            |                                                              |
 *            V                                                              V
 *        SCHEDULED ----------------------------->---------------------------+
 *            |                                                              |
 *            V                                                              V
 *  +---> WAITING ON RECIPIENT ------------------->--------------------------+
 *  |         |                                                              |
 *  |         V                                                              V
 *  | +-> WAITING ON SENDER ---------------------->--------------------------+
 *  | |       |                                                              |
 *  ^ |       V                                                              V
 *  | +-- CAPTURING ------------------------------>--------------------------+
 *  |         |                                                              |
 *  |         V                                                              V
 *  +---- WAITING ON RISK ------------------------>--------------------------+
 *  |         |                                                              |
 *  |         V                                                              V
 *  |     WAITING TO PAYOUT ---------------------->--------------------------+
 *  |         |                                                              |
 *  |         V                                                              V
 *  +<---- PAYING -------------------------------->--------------------------+
 *  |        OUT  <--------------------------------------------+             |
 *  ^         |                                                |             |
 *  |         +--<-----------------+--<-----------+--<------+  |             |
 *  |         |                    ^              ^          \ |             |
 *  |         V                    |              |           \|             V
 *  +--- RECLAIMING CAPTURE     WAITING ON --> PULLING --> WAITING TO <-> REFUNDING -> RECLAIMING
 *  |         ^         \        REFUNDER       REFUND        REFUND         ^         REFUND PUSH
 *  ^         |          \         ^                                         |             ^
 *  |         V           \        |                                         |             |
 *  |    ************     V        |                                         |             V
 *  +--- * PAID OUT * ----+------>-+--------------------------------------->-+           FAILED
 *       ************
 *
 * CAPTURING, PULLING REFUND, PAYING OUT, REFUNDING, RECLAIMING CAPTURE,
 * and RECLAIMING REFUND PUSH are transitional states. When the system is operating normally, all
 * payments in these states will be quickly transitioned to another state.
 *
 * REFUNDING and FAILED payments typically have a non-null failure reason.
 *
 * Payments that are VALIDATING, WAITING_ON_SENDER, CAPTURING, or
 * WAITING_ON_RECIPIENT are eligible for expiration. When a payment expires
 * it moves to either REFUNDING or FAILED.
 */
export enum PaymentState {
  /**
   * VALIDATING - The payment email was added to the database as one or more payments.
   * Validation on all the input is still pending.
   */
  VALIDATING = "VALIDATING",
  /**
   * SCHEDULED - The payment was created to be sent at a later time. This is used to delay the payment
   * either for future scheduled payments or to create it in advance of recurring payments.
   */
  SCHEDULED = "SCHEDULED",
  /** WAITING_ON_SENDER - We're waiting on the sender to confirm, provide account information or upgrade. */
  WAITING_ON_SENDER = "WAITING_ON_SENDER",
  /** CAPTURING - We're authorizing and capturing the sender's money. */
  CAPTURING = "CAPTURING",
  /** WAITING_ON_RECIPIENT - We're waiting on the recipient to provide account information or upgrade. */
  WAITING_ON_RECIPIENT = "WAITING_ON_RECIPIENT",
  /** WAITING_ON_RISK - We're waiting on risk to approve or block this payment. */
  WAITING_ON_RISK = "WAITING_ON_RISK",
  /**
   * WAITING_TO_PAY_OUT - The payment has been approved by risk, but isn't ready to be paid out. We
   * hold pay outs for various reasons. For example, risk uses this so that
   * they may later retract their approval. Payroll uses it to control when an
   * employee receives funds.
   */
  WAITING_TO_PAY_OUT = "WAITING_TO_PAY_OUT",
  /** PAYING_OUT - We're in the process of moving money the recipient's account. */
  PAYING_OUT = "PAYING_OUT",
  /**
   * PAID_OUT - The payment is complete.
   *
   * A payment that has been paid out can be reversed if the receiving bank
   * reports a delayed error. When this occurs, the payment goes back to the
   * WAITING_ON_RECIPIENT state where the payout can be retried.
   *
   * This is the terminal state for successful payments.
   */
  PAID_OUT = "PAID_OUT",
  /**
   * WAITING_ON_REFUNDER - The recipient has attempted to refund this payment. The first time that a
   * payment reaches this state, it should advance past it without getting stuck.
   * If a refund pull fails, the payment will retreat to this state to give the
   * refunder the opportunity to change their instrument and try again.
   */
  WAITING_ON_REFUNDER = "WAITING_ON_REFUNDER",
  /**
   * PULLING_REFUND - We're pulling the funds back from the recipient such that they can be
   * refunded to the sender.
   */
  PULLING_REFUND = "PULLING_REFUND",
  /**
   * WAITING_TO_REFUND - If the payment is being refunded via ACH, we need to delay 3 business
   * days to ensure that the ACH transfer doesn't fail. During that delay, the
   * payment will be kept in this state.
   */
  WAITING_TO_REFUND = "WAITING_TO_REFUND",
  /**
   * REFUNDING - The payment has failed and funds must be returned to the sender. Once
   * funds have been refunded this payment will enter the FAILED state.
   */
  REFUNDING = "REFUNDING",
  /** FAILED - The payment has failed. If captured, the sender's funds have been refunded. */
  FAILED = "FAILED",
  /**
   * RECLAIMING_CAPTURE - The capture of the sender's money was executed asynchronously and subsequently failed. This
   * might happen for ACH pulls. We are reclaiming the funds from the sender by pulling from
   * the stored balance. If there is not enough funds, then this results in a negative balance.
   *
   * Reclaim happens after payment advances to PAID_OUT.
   */
  RECLAIMING_CAPTURE = "RECLAIMING_CAPTURE",
  /**
   * RECLAIMING_REFUND_PUSH - The payment was cancelled after the capture of the sender's money was initiated. We waited
   * for capture to tentatively succeed and refunded money to the sender as a refund push. This
   * might happen for ACH pulls. But later the capture that we already refunded has failed. We are
   * reclaiming the refund push from the sender by pulling from the stored balance. If there is
   * not enough funds, then this results in a negative balance.
   *
   * Reclaim happens after payment advances to FAILED.
   */
  RECLAIMING_REFUND_PUSH = "RECLAIMING_REFUND_PUSH",
}

export enum PaymentFailureReason {
  /** EXPIRED - The payment was not paid out within the required time. Deprecated. */
  EXPIRED = "EXPIRED",
  /** REJECTED - The recipient refused this payment. */
  REJECTED = "REJECTED",
  /** REVOKED - The sender revoked this payment. */
  REVOKED = "REVOKED",
  /**
   * BLOCKED - Fraud blocked this payment. To avoid information leak, blocked payments
   * should be messaged as if they were declined.
   * Deprecated by AUTOMATICALLY_BLOCKED and MANUALLY_BLOCKED.
   */
  BLOCKED = "BLOCKED",
  /** DECLINED - The sender's bank declined this payment. */
  DECLINED = "DECLINED",
  /** SENDER_NOT_INVITED - The payment sender is not registered. */
  SENDER_NOT_INVITED = "SENDER_NOT_INVITED",
  /** SENDER_NO_LINKED_CARD - The sender doesn't have a linked card. */
  SENDER_NO_LINKED_CARD = "SENDER_NO_LINKED_CARD",
  /** SENDER_BLACKLISTED - The sender is denylisted. */
  SENDER_BLACKLISTED = "SENDER_BLACKLISTED",
  SENDER_DENYLISTED = "SENDER_DENYLISTED",
  /** RECIPIENT_INVALID - The destination email address is invalid. */
  RECIPIENT_INVALID = "RECIPIENT_INVALID",
  /** RECIPIENT_BLACKLISTED - The recipient is denylisted. */
  RECIPIENT_BLACKLISTED = "RECIPIENT_BLACKLISTED",
  RECIPIENT_DENYLISTED = "RECIPIENT_DENYLISTED",
  /** RECIPIENT_NOT_FOUND - The recipient was not found. (No getter for a cash@ payment.) */
  RECIPIENT_NOT_FOUND = "RECIPIENT_NOT_FOUND",
  /** RECIPIENT_TOO_MANY - More than one recipient. */
  RECIPIENT_TOO_MANY = "RECIPIENT_TOO_MANY",
  /** SUBJECT_INVALID - Couldn't find amount in subject line. */
  SUBJECT_INVALID = "SUBJECT_INVALID",
  /** AMOUNT_EXCEEDED_SINGLE_TRANSACTION_LIMIT - The requested amount is higher than maximum single transaction. */
  AMOUNT_EXCEEDED_SINGLE_TRANSACTION_LIMIT = "AMOUNT_EXCEEDED_SINGLE_TRANSACTION_LIMIT",
  /** AMOUNT_BELOW_MINIMUM_TRANSACTION_LIMIT - The requested amount is lower than minimum single transaction. */
  AMOUNT_BELOW_MINIMUM_TRANSACTION_LIMIT = "AMOUNT_BELOW_MINIMUM_TRANSACTION_LIMIT",
  /** AMOUNT_EXCEEDED_DAILY_TRANSACTION_LIMIT - With the requested amount, the sender exceeds their daily transaction limit. */
  AMOUNT_EXCEEDED_DAILY_TRANSACTION_LIMIT = "AMOUNT_EXCEEDED_DAILY_TRANSACTION_LIMIT",
  /**
   * SENDER_DENIED - We asked the sender to confirm that this was a legitimate payment, and
   * she denied it. This may occur if a payment email is forged. This reason
   * should be used for payments that expire while in the WAITING_ON_SENDER
   * state.
   */
  SENDER_DENIED = "SENDER_DENIED",
  /** SENDER_NON_MTL - Square lacks a Money Transmitter License (MTL) in the sender's state. */
  SENDER_NON_MTL = "SENDER_NON_MTL",
  /** AMOUNT_EXCEEDED_WEEKLY_TRANSACTION_LIMIT - With the requested amount, the sender exceeds their weekly transaction limit. */
  AMOUNT_EXCEEDED_WEEKLY_TRANSACTION_LIMIT = "AMOUNT_EXCEEDED_WEEKLY_TRANSACTION_LIMIT",
  /** AMOUNT_EXCEEDED_MONTHLY_TRANSACTION_LIMIT - With the requested amount, the sender exceeds their monthly transaction limit. */
  AMOUNT_EXCEEDED_MONTHLY_TRANSACTION_LIMIT = "AMOUNT_EXCEEDED_MONTHLY_TRANSACTION_LIMIT",
  /** AMOUNT_EXCEEDED_RECIPIENT_WEEKLY_RECEIVE_LIMIT - The payment amount exceeds the recipient's weekly receive limit. */
  AMOUNT_EXCEEDED_RECIPIENT_WEEKLY_RECEIVE_LIMIT = "AMOUNT_EXCEEDED_RECIPIENT_WEEKLY_RECEIVE_LIMIT",
  /** AMOUNT_EXCEEDED_RECIPIENT_MONTHLY_RECEIVE_LIMIT - The payment amount exceeds the recipient's monthly receive limit. */
  AMOUNT_EXCEEDED_RECIPIENT_MONTHLY_RECEIVE_LIMIT = "AMOUNT_EXCEEDED_RECIPIENT_MONTHLY_RECEIVE_LIMIT",
  /** RECIPIENT_NOT_IN_PILOT - The destination email address isn't eligible yet. */
  RECIPIENT_NOT_IN_PILOT = "RECIPIENT_NOT_IN_PILOT",
  /** SENDER_CARD_EXPIRED - The sender's card is expired. */
  SENDER_CARD_EXPIRED = "SENDER_CARD_EXPIRED",
  /** SENDER_NOT_IN_PILOT - The sender email address isn't eligible yet. */
  SENDER_NOT_IN_PILOT = "SENDER_NOT_IN_PILOT",
  /** EMAIL_IS_A_BOUNCE - The received email is a bounce i.e. return-path is "<>" as per RFC3834, drop silently. */
  EMAIL_IS_A_BOUNCE = "EMAIL_IS_A_BOUNCE",
  /** EMAIL_WAS_SENT_FROM_LIST - We silently drop emails from mailing lists. */
  EMAIL_WAS_SENT_FROM_LIST = "EMAIL_WAS_SENT_FROM_LIST",
  /** EMAIL_WAS_AN_AUTORESPONSE - We silently drop emails sent by auto-responders. */
  EMAIL_WAS_AN_AUTORESPONSE = "EMAIL_WAS_AN_AUTORESPONSE",
  /** EMAIL_WAS_SPOOFED - We silently drop emails that are clearly spoofed. */
  EMAIL_WAS_SPOOFED = "EMAIL_WAS_SPOOFED",
  /** CUSTOMER_CANNOT_INITIATE_VIA_EMAIL - This customer cannot initiate via email. */
  CUSTOMER_CANNOT_INITIATE_VIA_EMAIL = "CUSTOMER_CANNOT_INITIATE_VIA_EMAIL",
  /** PAY_SQUAREUP_EMPLOYEE - The sender is a Square and they used pay@squareup.com. */
  PAY_SQUAREUP_EMPLOYEE = "PAY_SQUAREUP_EMPLOYEE",
  /** EMAIL_TO_RECIPIENT_BOUNCED - Email sent to recipient bounced. */
  EMAIL_TO_RECIPIENT_BOUNCED = "EMAIL_TO_RECIPIENT_BOUNCED",
  /** UPGRADE_WITH_TLO_REJECTED - The customer failed to verify his or her identity with TLO. */
  UPGRADE_WITH_TLO_REJECTED = "UPGRADE_WITH_TLO_REJECTED",
  /** SENDER_FAILED_IDENTITY_VERIFICATION - The sender failed to verify his or her identity. */
  SENDER_FAILED_IDENTITY_VERIFICATION = "SENDER_FAILED_IDENTITY_VERIFICATION",
  /** RECIPIENT_FAILED_IDENTITY_VERIFICATION - The recipient failed to verify his or her identity. */
  RECIPIENT_FAILED_IDENTITY_VERIFICATION = "RECIPIENT_FAILED_IDENTITY_VERIFICATION",
  /** SENDER_NO_MORE_IDV_ATTEMPTS_LEFT - The sender was involved in a payment that required IDV but did not have attempts left. */
  SENDER_NO_MORE_IDV_ATTEMPTS_LEFT = "SENDER_NO_MORE_IDV_ATTEMPTS_LEFT",
  /** RECIPIENT_NO_MORE_IDV_ATTEMPTS_LEFT - The recipient was involved in a payment that required IDV but did not have attempts left. */
  RECIPIENT_NO_MORE_IDV_ATTEMPTS_LEFT = "RECIPIENT_NO_MORE_IDV_ATTEMPTS_LEFT",
  /** SENT_TO_SELF - The sender attempted to send cash to himself. */
  SENT_TO_SELF = "SENT_TO_SELF",
  /** SYSTEM_OFFLINE - Franklin was disabled when the payment was received. */
  SYSTEM_OFFLINE = "SYSTEM_OFFLINE",
  /** SENDER_CHARGEBACK - Franklin received a notice that the sender charged back the payment pull. */
  SENDER_CHARGEBACK = "SENDER_CHARGEBACK",
  /** EXCEEDED_PASSCODE_ATTEMPTS - The sender failed to enter a correct passcode within the attempts limit. */
  EXCEEDED_PASSCODE_ATTEMPTS = "EXCEEDED_PASSCODE_ATTEMPTS",
  /** BLOCKED_BY_CVV_ATTEMPTS - The sender card was blocked due to too many invalid CVV attempts. */
  BLOCKED_BY_CVV_ATTEMPTS = "BLOCKED_BY_CVV_ATTEMPTS",
  /** LACKS_BILL_FEATURE - The sender or recipient require an unreleased feature to complete this payment. */
  LACKS_BILL_FEATURE = "LACKS_BILL_FEATURE",
  /** BILL_GETTER_REFUSED - The bill getter refused to pay this bill. */
  BILL_GETTER_REFUSED = "BILL_GETTER_REFUSED",
  /** BILL_INITIATOR_RETRACT - The bill initiator didn't want this money after all. */
  BILL_INITIATOR_RETRACT = "BILL_INITIATOR_RETRACT",
  /** SQUAREUP_DOMAIN - The sender attempted to use pay@squareup.com or cash@squareup.com. */
  SQUAREUP_DOMAIN = "SQUAREUP_DOMAIN",
  /**
   * MANUALLY_REVERSED - Square employees manually reversed this payment. Square can lose money
   * on these payments because they can be both paid out to the recipient and
   * refunded to the sender. We do them when a customer writes in to complain
   * that funds were not delivered.
   */
  MANUALLY_REVERSED = "MANUALLY_REVERSED",
  /** EXCEEDED_SENDER_STATE_LIMIT - The payment amount exceeded the single payment limit in the sender's State. */
  EXCEEDED_SENDER_STATE_LIMIT = "EXCEEDED_SENDER_STATE_LIMIT",
  /** EXCEEDED_RECIPIENT_STATE_LIMIT - The payment amount exceeded the single payment limit in the recipient's State. */
  EXCEEDED_RECIPIENT_STATE_LIMIT = "EXCEEDED_RECIPIENT_STATE_LIMIT",
  /** SENDER_NOT_FOUND - The sender was not found. (No getter for a bill@ payment.) */
  SENDER_NOT_FOUND = "SENDER_NOT_FOUND",
  /** ACH_PAYOUT_FAILED_ASYNCHRONOUSLY - A payment paid out via ACH was reversed by reconciliation. */
  ACH_PAYOUT_FAILED_ASYNCHRONOUSLY = "ACH_PAYOUT_FAILED_ASYNCHRONOUSLY",
  /** EXPIRED_WAITING_ON_SENDER - The sender did not provide requested information and the payment expired. */
  EXPIRED_WAITING_ON_SENDER = "EXPIRED_WAITING_ON_SENDER",
  /** EXPIRED_ON_SENDER_WITH_RISK_BLOCKERS - The sender did not resolve the risk blockers and the payment expired. */
  EXPIRED_ON_SENDER_WITH_RISK_BLOCKERS = "EXPIRED_ON_SENDER_WITH_RISK_BLOCKERS",
  /** EXPIRED_WAITING_ON_RECIPIENT - The recipient did not provide requested information and the payment expired. */
  EXPIRED_WAITING_ON_RECIPIENT = "EXPIRED_WAITING_ON_RECIPIENT",
  /** EXPIRED_ON_RECIPIENT_WITH_RISK_BLOCKERS - The recipient did not resolve the risk blockers and the payment expired. */
  EXPIRED_ON_RECIPIENT_WITH_RISK_BLOCKERS = "EXPIRED_ON_RECIPIENT_WITH_RISK_BLOCKERS",
  /** NO_SQUARE_ADDRESS - The customer BCC'd Square and we don't know if it's bill@ or cash@. */
  NO_SQUARE_ADDRESS = "NO_SQUARE_ADDRESS",
  /** TRY_IT_NOW_CUSTOMER_ALREADY_A_CUSTOMER - Customers that we send a $1 to 'try it now' must not have used Cash before. */
  TRY_IT_NOW_CUSTOMER_ALREADY_A_CUSTOMER = "TRY_IT_NOW_CUSTOMER_ALREADY_A_CUSTOMER",
  /** TRY_IT_NOW_PAYMENT_ABOVE_LIMIT - A safety hard-limit to stop any T.I.N. payments attempting to pay too much. */
  TRY_IT_NOW_PAYMENT_ABOVE_LIMIT = "TRY_IT_NOW_PAYMENT_ABOVE_LIMIT",
  /** CASH_PAYMENT_WITH_REQUEST_SUBJECT - An email to cash@square.com has \"request\" in the subject. */
  CASH_PAYMENT_WITH_REQUEST_SUBJECT = "CASH_PAYMENT_WITH_REQUEST_SUBJECT",
  /** EXCEEDED_BILL_OUTSTANDING_LIMIT - The total requested for a bill added to outstanding bills exceeds the outstanding limit. */
  EXCEEDED_BILL_OUTSTANDING_LIMIT = "EXCEEDED_BILL_OUTSTANDING_LIMIT",
  /** RECIPIENT_FIDELIUS_TOKEN_TOO_MANY_PAYMENTS - The recipient fidelius token has received too many payments. Reject them immediately. */
  RECIPIENT_FIDELIUS_TOKEN_TOO_MANY_PAYMENTS = "RECIPIENT_FIDELIUS_TOKEN_TOO_MANY_PAYMENTS",
  /** TRY_IT_NOW_PAYMENT_NOT_VALID_FOR_ACH - Customers that we send a $1 to 'try it now' can't receive on bank account. */
  TRY_IT_NOW_PAYMENT_NOT_VALID_FOR_ACH = "TRY_IT_NOW_PAYMENT_NOT_VALID_FOR_ACH",
  /** BILL_GETTER_INVALID - Bill getter's email address is invalid. */
  BILL_GETTER_INVALID = "BILL_GETTER_INVALID",
  /**
   * BILL_GETTER_UNSUPPORTED - Bill getter does not support requests.  For example, requests to a customer running a
   * political campaign are not allowed.
   */
  BILL_GETTER_UNSUPPORTED = "BILL_GETTER_UNSUPPORTED",
  /**
   * DEPOSIT_MECHANISMS_EXHAUSTED - Payment failed and dropped instead of returning to waiting on recipient to link a new
   * instrument after all deposit mechanisms were attempted.
   */
  DEPOSIT_MECHANISMS_EXHAUSTED = "DEPOSIT_MECHANISMS_EXHAUSTED",
  /** BOUNTY_REFERRER_BLACKLISTED - A bounty payout has been canceled because the referrer is denylisted. */
  BOUNTY_REFERRER_BLACKLISTED = "BOUNTY_REFERRER_BLACKLISTED",
  BOUNTY_REFERRER_DENYLISTED = "BOUNTY_REFERRER_DENYLISTED",
  /** RECIPIENT_CARD_BLACKLISTED - We refuse a payout because the recipient's current linked card is denylisted. */
  RECIPIENT_CARD_BLACKLISTED = "RECIPIENT_CARD_BLACKLISTED",
  RECIPIENT_CARD_DENYLISTED = "RECIPIENT_CARD_DENYLISTED",
  /** RECIPIENT_BANK_ACCOUNT_BLACKLISTED - We refuse a payout because the recipient's current bank account is denylisted. */
  RECIPIENT_BANK_ACCOUNT_BLACKLISTED = "RECIPIENT_BANK_ACCOUNT_BLACKLISTED",
  RECIPIENT_BANK_ACCOUNT_DENYLISTED = "RECIPIENT_BANK_ACCOUNT_DENYLISTED",
  /** SENDER_CARD_BLACKLISTED - We refuse a payout or capture because the sender's current linked card is denylisted. */
  SENDER_CARD_BLACKLISTED = "SENDER_CARD_BLACKLISTED",
  SENDER_CARD_DENYLISTED = "SENDER_CARD_DENYLISTED",
  /** EMAIL_DOMAIN_BLACKLISTED - Email domain of either the sender or the recipient has been denylisted. */
  EMAIL_DOMAIN_BLACKLISTED = "EMAIL_DOMAIN_BLACKLISTED",
  EMAIL_DOMAIN_DENYLISTED = "EMAIL_DOMAIN_DENYLISTED",
  /** AUTOMATICALLY_BLOCKED - Blocked by an automatic risk system. */
  AUTOMATICALLY_BLOCKED = "AUTOMATICALLY_BLOCKED",
  /** MANUALLY_BLOCKED - Blocked by a risk operations person. */
  MANUALLY_BLOCKED = "MANUALLY_BLOCKED",
  /** AMOUNT_EXCEEDED_BUSINESS_LIMIT - With the requested amount, the sender exceeds their limit sending to businesses. */
  AMOUNT_EXCEEDED_BUSINESS_LIMIT = "AMOUNT_EXCEEDED_BUSINESS_LIMIT",
  /** SENDER_EXCEEDED_VERIFICATION_ATTEMPTS - The sender failed to enter a correct verification code within the attempts limit. */
  SENDER_EXCEEDED_VERIFICATION_ATTEMPTS = "SENDER_EXCEEDED_VERIFICATION_ATTEMPTS",
  /** RECIPIENT_EXCEEDED_VERIFICATION_ATTEMPTS - The recipient failed to enter a correct verification code within the attempts limit. */
  RECIPIENT_EXCEEDED_VERIFICATION_ATTEMPTS = "RECIPIENT_EXCEEDED_VERIFICATION_ATTEMPTS",
  /**
   * RECIPIENT_REFUNDED - The payment was refunded by the recipient after being paid out. The funds were pulled back
   * from the recipient before reversing the sender's push.
   */
  RECIPIENT_REFUNDED = "RECIPIENT_REFUNDED",
  /**
   * MANUALLY_REFUNDED - The payment was refunded by an admin. The funds were pulled back from the recipient before
   * reversing the sender's push.
   */
  MANUALLY_REFUNDED = "MANUALLY_REFUNDED",
  /** CHARGEBACK_REFUNDED - The payment was refunded to Square because the sender issued a chargeback. */
  CHARGEBACK_REFUNDED = "CHARGEBACK_REFUNDED",
  /** CHARGEBACK_REFUNDED_FORCED_STORED_VALUE - The payment was refunded to Square because the sender issued a chargeback. */
  CHARGEBACK_REFUNDED_FORCED_STORED_VALUE = "CHARGEBACK_REFUNDED_FORCED_STORED_VALUE",
  /**
   * UNLINKED_REFUND_ASYNCHRONOUSLY_FAILED - Unlinked refund asynchronously failed. Due to quirks in Wells reporting, these come to us
   * in the form of chargebacks with a negative dispute amount. While these payments are reported
   * through the chargebacks systems, they have nothing to do with real chargebacks.
   */
  UNLINKED_REFUND_ASYNCHRONOUSLY_FAILED = "UNLINKED_REFUND_ASYNCHRONOUSLY_FAILED",
  /**
   * MANUALLY_FAILED_PAYMENT - The payment was manually failed by an on-call engineer through the dashboard.
   * This is distinguished from *BLOCKED which are specifically risk-related block reasons.
   */
  MANUALLY_FAILED_PAYMENT = "MANUALLY_FAILED_PAYMENT",
  /**
   * SENDER_BALANCE_NEGATIVE - The payment was failed because the sender has a negative stored value balance. We prevent
   * the sender from initializing payments and paying bills until the stored value balance is
   * positive.
   */
  SENDER_BALANCE_NEGATIVE = "SENDER_BALANCE_NEGATIVE",
  /** SENDER_WAS_REPORTED_ABUSIVE - The payment failed because the sender was reported abusive. */
  SENDER_WAS_REPORTED_ABUSIVE = "SENDER_WAS_REPORTED_ABUSIVE",
  /** RECIPIENT_WAS_REPORTED_ABUSIVE - The payment failed because the recipient was reported abusive. */
  RECIPIENT_WAS_REPORTED_ABUSIVE = "RECIPIENT_WAS_REPORTED_ABUSIVE",
  /** SENDER_ORPHANED - The payment failed because the sender has no active email or SMS aliases. */
  SENDER_ORPHANED = "SENDER_ORPHANED",
  /** RECIPIENT_ORPHANED - The payment failed because the recipient has no active email or SMS aliases. */
  RECIPIENT_ORPHANED = "RECIPIENT_ORPHANED",
  /**
   * REFUNDING_TO_BALANCE - An admin explicitly chose to "refund" a payment back into the *recipient's* balance.
   * Useful when the customer no longer has access to the card we paid out to.
   */
  REFUNDING_TO_BALANCE = "REFUNDING_TO_BALANCE",
  /**
   * EXCEEDED_RETRY_EXPIRATION - An offline payment was retried much later (configurable) than it was originally attempted
   * the app.
   */
  EXCEEDED_RETRY_EXPIRATION = "EXCEEDED_RETRY_EXPIRATION",
  /** CROSS_BORDER - The payment failed because it was cross border. */
  CROSS_BORDER = "CROSS_BORDER",
  /** EXCEEDED_PULL_ATTEMPTS - The payment exceeded the maximum number of pull attempts. */
  EXCEEDED_PULL_ATTEMPTS = "EXCEEDED_PULL_ATTEMPTS",
  /** REQUIRES_PREVIOUS_SUCCESS - Payment requires that a previous payment with recipient to have succeeded. */
  REQUIRES_PREVIOUS_SUCCESS = "REQUIRES_PREVIOUS_SUCCESS",
  /**
   * TOO_MANY_REFERRAL_REWARDS - Customers should only ever receive one referral reward payment: it's a bonus for signing up
   * to Cash App. If we detect that a customer has received multiple rewards (perhaps due to a
   * customer merge or from an IDV result), we cancel the extra payments with this reason.
   */
  TOO_MANY_REFERRAL_REWARDS = "TOO_MANY_REFERRAL_REWARDS",
  /** SENDER_SUSPENDED - Suspended customers should not be able to send money. */
  SENDER_SUSPENDED = "SENDER_SUSPENDED",
  /** RECIPIENT_SUSPENDED - Suspended customers should not be able to receive money. */
  RECIPIENT_SUSPENDED = "RECIPIENT_SUSPENDED",
  /** ENFORCED_CROSS_BORDER_TRAVEL_RULE - The payment was cancelled to enforce travel rule for US customers. */
  ENFORCED_CROSS_BORDER_TRAVEL_RULE = "ENFORCED_CROSS_BORDER_TRAVEL_RULE",
  /**
   * NOT_IN_CROSS_BORDER_EXPERIMENT - Multi-currency and cross-border payments are controlled by the same feature flag.
   * The flag was not turned on for the customer.
   */
  NOT_IN_CROSS_BORDER_EXPERIMENT = "NOT_IN_CROSS_BORDER_EXPERIMENT",
  /** CROSS_BORDER_NOT_ALLOWED_FOR_BUSINESS - Business customers are not allowed to make cross-border payments. */
  CROSS_BORDER_NOT_ALLOWED_FOR_BUSINESS = "CROSS_BORDER_NOT_ALLOWED_FOR_BUSINESS",
  /** INITIATOR_NOT_VERIFIED_FOR_CROSS_BORDER - Only verified Gold customers are allowed to make cross-border payments. */
  INITIATOR_NOT_VERIFIED_FOR_CROSS_BORDER = "INITIATOR_NOT_VERIFIED_FOR_CROSS_BORDER",
  /** CANNOT_CREATE_EXCHANGE_QUOTE - There is no valid exchange quote for the desired cross-border payment. */
  CANNOT_CREATE_EXCHANGE_QUOTE = "CANNOT_CREATE_EXCHANGE_QUOTE",
  /** CROSS_BORDER_APP_RELEASE_TOO_OLD - The app release version does not have the required capability for cross-border payments. */
  CROSS_BORDER_APP_RELEASE_TOO_OLD = "CROSS_BORDER_APP_RELEASE_TOO_OLD",
  /** CROSS_BORDER_CASH_IN_FAILED - A cash in was required to complete this payment, but the cash in failed. */
  CROSS_BORDER_CASH_IN_FAILED = "CROSS_BORDER_CASH_IN_FAILED",
  /**
   * CROSS_BORDER_CREDIT_CARD_FUNDING_NOT_ALLOWED - Credit card is not allowed as a source instrument for cross border payments as it is
   * contradicting to see no fee in exchange rate blocker and credit card pulls involving fee.
   */
  CROSS_BORDER_CREDIT_CARD_FUNDING_NOT_ALLOWED = "CROSS_BORDER_CREDIT_CARD_FUNDING_NOT_ALLOWED",
  /**
   * CROSS_BORDER_TO_AUTO_CASH_OUT_RECIPIENT - Cross border payments are only allowed between stored balances and auto cashouts break this
   * rule.
   */
  CROSS_BORDER_TO_AUTO_CASH_OUT_RECIPIENT = "CROSS_BORDER_TO_AUTO_CASH_OUT_RECIPIENT",
  /** AMOUNT_EXCEEDED_CROSS_BORDER_WEEKLY_SEND_LIMIT - With the requested amount, the sender exceeds their cross border weekly send limit. */
  AMOUNT_EXCEEDED_CROSS_BORDER_WEEKLY_SEND_LIMIT = "AMOUNT_EXCEEDED_CROSS_BORDER_WEEKLY_SEND_LIMIT",
  /** AMOUNT_EXCEEDED_CROSS_BORDER_WEEKLY_RECEIVE_LIMIT - With the requested amount, the recipient exceeds their cross border weekly receive limit. */
  AMOUNT_EXCEEDED_CROSS_BORDER_WEEKLY_RECEIVE_LIMIT = "AMOUNT_EXCEEDED_CROSS_BORDER_WEEKLY_RECEIVE_LIMIT",
  /** API_HARD_FAIL - Automatically failed by the API because the payment had an unskippable blocker. */
  API_HARD_FAIL = "API_HARD_FAIL",
  /** AMOUNT_EXCEEDED_TIPPING_MONTHLY_SEND_LIMIT - Including the requested amount, the sender exceeds their tipping monthly send limit. */
  AMOUNT_EXCEEDED_TIPPING_MONTHLY_SEND_LIMIT = "AMOUNT_EXCEEDED_TIPPING_MONTHLY_SEND_LIMIT",
  /**
   * THREE_DOMAIN_SECURE_CHALLENGE_WEB_UNSUPPORTED - Not supporting completing 3ds challenge on web to avoid dealing with redirection and form
   * blockers.
   * More info on 3DS https://docs.google.com/document/d/1IckbS1fpSJ1sYbqjqotR9DrFNlChORELC6pCcy0aWkA/edit#
   */
  THREE_DOMAIN_SECURE_CHALLENGE_WEB_UNSUPPORTED = "THREE_DOMAIN_SECURE_CHALLENGE_WEB_UNSUPPORTED",
  /**
   * THREE_DOMAIN_SECURE_CHALLENGE_FAILED - We may challenge senders to complete a 3DS challenge for 3DS-enabled instruments in selected regions.
   * The sender fails the 3DS challenge, which means that they have failed to authenticate with their issuer.
   */
  THREE_DOMAIN_SECURE_CHALLENGE_FAILED = "THREE_DOMAIN_SECURE_CHALLENGE_FAILED",
  /** SPONSORED_ACCOUNT_BALANCE_EXCEEDED - Stored balance exceeded for sponsored account */
  SPONSORED_ACCOUNT_BALANCE_EXCEEDED = "SPONSORED_ACCOUNT_BALANCE_EXCEEDED",
  /** NOT_AVAILABLE_FOR_PENDING_SPONSORED_ACCOUNTS - Action not available for pending sponsored accounts */
  NOT_AVAILABLE_FOR_PENDING_SPONSORED_ACCOUNTS = "NOT_AVAILABLE_FOR_PENDING_SPONSORED_ACCOUNTS",
  /** NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS - Action not available for sponsored accounts */
  NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS = "NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS",
  /**
   * SPONSORSHIP_REQUESTED - Customer requested a sponsorship while initiating this payment. And the payment was cancelled
   * because of the new pending sponsorship. Customer can try again once the sponsorship is
   * approved.
   */
  SPONSORSHIP_REQUESTED = "SPONSORSHIP_REQUESTED",
  /** UNDER_SPONSORSHIP_ELIGIBLE_AGE - Customer is under 13. */
  UNDER_SPONSORSHIP_ELIGIBLE_AGE = "UNDER_SPONSORSHIP_ELIGIBLE_AGE",
  /** EXCEEDED_SPONSORSHIP_REQUEST_LIMIT - Customer exceeded the sponsorship request limit. */
  EXCEEDED_SPONSORSHIP_REQUEST_LIMIT = "EXCEEDED_SPONSORSHIP_REQUEST_LIMIT",
  /** CROSS_BORDER_PAYMENT_SPONSORED_ACCOUNTS - Sponsored accounts cannot send cross border payment. */
  CROSS_BORDER_PAYMENT_SPONSORED_ACCOUNTS = "CROSS_BORDER_PAYMENT_SPONSORED_ACCOUNTS",
  /** INVESTING_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS - Investing not available for sponsored accounts */
  INVESTING_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS = "INVESTING_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS",
  /** BITCOIN_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS - Bitcoin not available for sponsored accounts */
  BITCOIN_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS = "BITCOIN_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS",
  /** REQUEST_SPONSORSHIP_APP_VERSION_UNSUPPORTED - The app release version does not support sponsorship. */
  REQUEST_SPONSORSHIP_APP_VERSION_UNSUPPORTED = "REQUEST_SPONSORSHIP_APP_VERSION_UNSUPPORTED",
  /** THREE_DOMAIN_SECURE_CHALLENGE_APP_RELEASE_TOO_OLD - App too old to complete required 3DS authentication */
  THREE_DOMAIN_SECURE_CHALLENGE_APP_RELEASE_TOO_OLD = "THREE_DOMAIN_SECURE_CHALLENGE_APP_RELEASE_TOO_OLD",
  /**
   * EXCEEDED_ACTIVE_LINKED_ACCOUNTS_LIMIT - Transaction blocked since the account has more than 10
   * linked active accounts (with the same SSN)
   */
  EXCEEDED_ACTIVE_LINKED_ACCOUNTS_LIMIT = "EXCEEDED_ACTIVE_LINKED_ACCOUNTS_LIMIT",
  /** SENDER_MONEY_MOVEMENT_BLOCKED - The sender is blocked from sending or transferring money. */
  SENDER_MONEY_MOVEMENT_BLOCKED = "SENDER_MONEY_MOVEMENT_BLOCKED",
  /** RECIPIENT_MONEY_MOVEMENT_BLOCKED - The sender is blocked from sending or transferring money. */
  RECIPIENT_MONEY_MOVEMENT_BLOCKED = "RECIPIENT_MONEY_MOVEMENT_BLOCKED",
  /** AMOUNT_EXCEEDED_COMBINED_MONTHLY_TRANSACTION_LIMIT - With the requested amount, the sender exceeds their monthly combined transaction limit. */
  AMOUNT_EXCEEDED_COMBINED_MONTHLY_TRANSACTION_LIMIT = "AMOUNT_EXCEEDED_COMBINED_MONTHLY_TRANSACTION_LIMIT",
  /** RECIPIENT_AMOUNT_EXCEEDED_WEEKLY_COMBINED_LIMIT - With the requested amount, the recipient exceeds their weekly send and receive limit. */
  RECIPIENT_AMOUNT_EXCEEDED_WEEKLY_COMBINED_LIMIT = "RECIPIENT_AMOUNT_EXCEEDED_WEEKLY_COMBINED_LIMIT",
  /** RECIPIENT_AMOUNT_EXCEEDED_MONTHLY_COMBINED_LIMIT - With the requested amount, the recipient exceeds their monthly send and receive limit. */
  RECIPIENT_AMOUNT_EXCEEDED_MONTHLY_COMBINED_LIMIT = "RECIPIENT_AMOUNT_EXCEEDED_MONTHLY_COMBINED_LIMIT",
  /** SENDER_AMOUNT_EXCEEDED_WEEKLY_COMBINED_LIMIT - With the requested amount, the sender exceeds their weekly send and receive limit. */
  SENDER_AMOUNT_EXCEEDED_WEEKLY_COMBINED_LIMIT = "SENDER_AMOUNT_EXCEEDED_WEEKLY_COMBINED_LIMIT",
  /** SENDER_AMOUNT_EXCEEDED_MONTHLY_COMBINED_LIMIT - With the requested amount, the sender exceeds their weekly send and receive limit. */
  SENDER_AMOUNT_EXCEEDED_MONTHLY_COMBINED_LIMIT = "SENDER_AMOUNT_EXCEEDED_MONTHLY_COMBINED_LIMIT",
  /** PAYMENT_BLOCKED_TO_AUTO_CASH_OUT_RECIPIENT - Payment is not allowed to the recipient until they turn off AutoCashOut */
  PAYMENT_BLOCKED_TO_AUTO_CASH_OUT_RECIPIENT = "PAYMENT_BLOCKED_TO_AUTO_CASH_OUT_RECIPIENT",
}

/** Reasons that a refund of a payment can fail. */
export enum PaymentRefundFailureReason {
  /** BANK_DECLINED - The refunder's bank declined the refund pull. */
  BANK_DECLINED = "BANK_DECLINED",
  /** REFUNDER_BLACKLISTED - The refunder is denylisted. */
  REFUNDER_BLACKLISTED = "REFUNDER_BLACKLISTED",
  REFUNDER_DENYLISTED = "REFUNDER_DENYLISTED",
  /** NO_VALID_PULL_INSTRUMENT - No available instrument to complete pull */
  NO_VALID_PULL_INSTRUMENT = "NO_VALID_PULL_INSTRUMENT",
  /**
   * INSTRUMENT_CHANGED - The refunder changed their instrument since this payment was deposited, which currently
   * prevents the ability to have it refunded. Long term we'd lke to lift this restriction.
   */
  INSTRUMENT_CHANGED = "INSTRUMENT_CHANGED",
  /** INSTRUMENT_EXPIRED - The refunder's instrument expired, which prevents our ability to pull the refund. */
  INSTRUMENT_EXPIRED = "INSTRUMENT_EXPIRED",
  /**
   * INSTRUMENT_INVALID - The refunder's instrument has had a permanent (non-recoverable) failure to authorize which
   * prevents our ability to pull the refund.
   */
  INSTRUMENT_INVALID = "INSTRUMENT_INVALID",
  /** REFUNDEE_BLACKLISTED - The refundee is denylisted. */
  REFUNDEE_BLACKLISTED = "REFUNDEE_BLACKLISTED",
  REFUNDEE_DENYLISTED = "REFUNDEE_DENYLISTED",
  /** MANUALLY_FAILED - The refund was forced to fail manually for some other reason. */
  MANUALLY_FAILED = "MANUALLY_FAILED",
  /**
   * REFUNDED_TO_BALANCE - The refund was forced by an admin into the *recipient's* balance,
   * not all the way back to the sender.
   */
  REFUNDED_TO_BALANCE = "REFUNDED_TO_BALANCE",
  /** REFUNDEE_SUSPENDED - The refundee is suspended. */
  REFUNDEE_SUSPENDED = "REFUNDEE_SUSPENDED",
  /** REFUNDER_SUSPENDED - The refunder is suspended. */
  REFUNDER_SUSPENDED = "REFUNDER_SUSPENDED",
  REFUNDER_MONEY_MOVEMENT_BLOCKED = "REFUNDER_MONEY_MOVEMENT_BLOCKED",
  REFUNDEE_MONEY_MOVEMENT_BLOCKED = "REFUNDEE_MONEY_MOVEMENT_BLOCKED",
}

/** Mechanism used to deposit/capture funds to/from a card or bank account. */
export enum PaymentMoneyMovingMechanism {
  UNKNOWN = "UNKNOWN",
  ADAPTIVE = "ADAPTIVE",
  EDJE = "EDJE",
  ACH = "ACH",
  MAESTRO = "MAESTRO",
  SQUARE_BALANCE = "SQUARE_BALANCE",
  ESPERANTO = "ESPERANTO",
  /**
   * ESPERANTO_DUAL_MESSAGE_AFT - Dual-Message AFT via Esperanto. This is substantially different from Single-Message AFT which
   * will be supported in the future.
   */
  ESPERANTO_DUAL_MESSAGE_AFT = "ESPERANTO_DUAL_MESSAGE_AFT",
  /** ESPERANTO_VISA_OCT - Visa OCT transactions via Esperanto. */
  ESPERANTO_VISA_OCT = "ESPERANTO_VISA_OCT",
  /** TELLER_ACH - Disburse to a bank account via Teller. */
  TELLER_ACH = "TELLER_ACH",
  /** ESPERANTO_VISA_OCT_FF - Visa OCT Fast Funds. (The instant version of OCT) */
  ESPERANTO_VISA_OCT_FF = "ESPERANTO_VISA_OCT_FF",
  /** ESPERANTO_STAR - STAR debit transactions via Esperanto. */
  ESPERANTO_STAR = "ESPERANTO_STAR",
  /** ACCULYNK - PINless debit and credit transactions via Acculynk. */
  ACCULYNK = "ACCULYNK",
  /**
   * ESPERANTO_SHAZAM - Shazam debit and credit transactions via Esperanto.
   * NOTE: Shazam does payment routing, so anything we send to them may be routed
   * to e.g. Shazam, PULSE, MAESTRO networks. We don't have control over the routing.
   */
  ESPERANTO_SHAZAM = "ESPERANTO_SHAZAM",
  /** ESPERANTO_NYCE_OVER_STAR - Nyce debit and credit transactions supported via Esperanto on STAR's network. */
  ESPERANTO_NYCE_OVER_STAR = "ESPERANTO_NYCE_OVER_STAR",
  /** BANKLIN - Stored balance and ledgering. */
  BANKLIN = "BANKLIN",
  /** LOB - Lob check sending platform. (banklin only) */
  LOB = "LOB",
  /**
   * STORED_VALUE - A virtual mechanism (banklin only) that works with standing stored value
   * balances in banklin.
   */
  STORED_VALUE = "STORED_VALUE",
  /**
   * ESPERANTO_SHAZAM_56 - Shazam credit transactions via Esperanto using the new Code 56 processing code.
   * We eventually want to deprecate the ESPERANTO_SHAZAM mechanism.
   */
  ESPERANTO_SHAZAM_56 = "ESPERANTO_SHAZAM_56",
  /** MASTERCARD_SEND - MasterCard's Person to Person Transfer (P2P) API. */
  MASTERCARD_SEND = "MASTERCARD_SEND",
  /** ACCULYNK_PAYSECURE - PINless debit and credit transactions via Acculynk Paysecure API (v2). */
  ACCULYNK_PAYSECURE = "ACCULYNK_PAYSECURE",
  /** MARQETA_ISSUED_CARD - Marqeta's prepaid system (issued virtual or physical card). */
  MARQETA_ISSUED_CARD = "MARQETA_ISSUED_CARD",
  /** SHAZAM_ISSUED_CARD - Shazam's Issuer Processing system (issued virtual or physical card). */
  SHAZAM_ISSUED_CARD = "SHAZAM_ISSUED_CARD",
  /** CORE_PRO_ACH - ACH transactions on CorePro/LSB (L)DDAs */
  CORE_PRO_ACH = "CORE_PRO_ACH",
  /** ESPERANTO_OMNIPAY_OCT - Omnipay OCT transactions via Esperanto */
  ESPERANTO_OMNIPAY_OCT = "ESPERANTO_OMNIPAY_OCT",
  /** XTS - XTS mechanism for shadow load payments */
  XTS = "XTS",
  /** MARQETA_ACH - ACH transactions on Marqeta DDAs. */
  MARQETA_ACH = "MARQETA_ACH",
  /** PANAMA - Panama support for card payment pulls */
  PANAMA = "PANAMA",
  /**
   * ESPERANTO_NYCE - Direct NYCE payments via Esperanto (direct as opposed to NYCE_OVER_STAR, which creates NYCE
   * payments but uses STAR's network to do it).
   */
  ESPERANTO_NYCE = "ESPERANTO_NYCE",
  /**
   * ESPERANTO_B2B - Back-to-back funding Visa payments via Esperanto (i.e. any payment where an initial pull from
   * a digital wallet, in our case stored balance, needs to be either partially or fully
   * supplemented in real-time by a pull from a card). Many of these payments have specific
   * business requirements (e.g. must be marked as Bitcoin Purchase, Loan Repayment, etc). We add
   * this as its own mechanism so that payments that code themselves using it can fall back to
   * other, pre-existing logic (e.g. ESPERANTO_DUAL_MESSAGE_AFT) if they encounter failures (as
   * acceptance rates could differ due to the specific codings introduced by this mechanism).
   */
  ESPERANTO_B2B = "ESPERANTO_B2B",
  /** MODERN_TREASURY_WIRE - Modern Treasury wire transactions. */
  MODERN_TREASURY_WIRE = "MODERN_TREASURY_WIRE",
}

/**
 * How the payment should be categorized for the purposes of coding the transaction as per
 * our contractual obligations with networks and other partners.
 */
export enum PaymentPaymentCodingCategory {
  /** P2P - A regular P2P transaction. */
  P2P = "P2P",
  /** DISBURSEMENT - A money disbursement transaction as defined by our contractual agreements. */
  DISBURSEMENT = "DISBURSEMENT",
  /** MERCHANT_SETTLEMENT - A transaction used to settle a Square Register merchant. */
  MERCHANT_SETTLEMENT = "MERCHANT_SETTLEMENT",
  /** COMMERCIAL - A commercial/merchant transaction (ie. Cash Pro pulls). */
  COMMERCIAL = "COMMERCIAL",
  /** PAYROLL_DISBURSEMENT - A payroll disbursement. */
  PAYROLL_DISBURSEMENT = "PAYROLL_DISBURSEMENT",
  /** REFERRAL - A Cash referral payment. */
  REFERRAL = "REFERRAL",
  /** COMMERCIAL_SETTLEMENT - A commercial/merchant transaction (ie. Cash Pro pushes). */
  COMMERCIAL_SETTLEMENT = "COMMERCIAL_SETTLEMENT",
  /** ISSUED_CARD_TRANSACTION - Issued card transaction. */
  ISSUED_CARD_TRANSACTION = "ISSUED_CARD_TRANSACTION",
  /** CAVIAR_SETTLEMENT - A Caviar courier or restaurant paid by Square. */
  CAVIAR_SETTLEMENT = "CAVIAR_SETTLEMENT",
  /** ACH_TRANSACTION - A Automated Clearing House transaction. */
  ACH_TRANSACTION = "ACH_TRANSACTION",
  /** CHECK_DEPOSIT - A Check Deposit transaction. */
  CHECK_DEPOSIT = "CHECK_DEPOSIT",
  /** INVEST_P2P_PAYMENT - A transaction performed for Invest-P2P payment flow. */
  INVEST_P2P_PAYMENT = "INVEST_P2P_PAYMENT",
  /** PHYSICAL_CASH_DEPOSIT - Physical cash deposit at a retailer through a partner like VDBS. */
  PHYSICAL_CASH_DEPOSIT = "PHYSICAL_CASH_DEPOSIT",
  /** PWC_PAYMENT - A transaction performed for Pay with Cash payment flow. */
  PWC_PAYMENT = "PWC_PAYMENT",
  /** INSTANT_PAY - A transaction performed for Instant Paycheck */
  INSTANT_PAY = "INSTANT_PAY",
  /** WIRE_TRANSACTION - A wire transaction. */
  WIRE_TRANSACTION = "WIRE_TRANSACTION",
  /** CRYPTO_TRANSFER - A transfer of cryptocurrency */
  CRYPTO_TRANSFER = "CRYPTO_TRANSFER",
  /** REMITTANCE_P2P_PAYMENT - An international remittance P2P transaction */
  REMITTANCE_P2P_PAYMENT = "REMITTANCE_P2P_PAYMENT",
  /** SAVINGS_TRANSFER - A transaction performed for Savings Transfer. */
  SAVINGS_TRANSFER = "SAVINGS_TRANSFER",
  /** OVERDRAFT - A transaction performed for Overdarft */
  OVERDRAFT = "OVERDRAFT",
  /** C4B_PAYMENT - A Cash for Business non-P2P payment, such as Tap to Pay. */
  C4B_PAYMENT = "C4B_PAYMENT",
  /** OVERDRAFT_MANUAL_REPAYMENT - A transaction performed for manual overdarft repayment */
  OVERDRAFT_MANUAL_REPAYMENT = "OVERDRAFT_MANUAL_REPAYMENT",
  /** OVERDRAFT_RECOUP_REPAYMENT - A transaction performed for repaying overdarft on delinquency */
  OVERDRAFT_RECOUP_REPAYMENT = "OVERDRAFT_RECOUP_REPAYMENT",
}

export interface PaymentStateTransition {
  id?: string;
  payment_id?: string;
  /** Unix time in millis */
  transitioned_at?: number;
  from_state?: PaymentState;
  to_state?: PaymentState;
}

/** A Franklin payment, simplified. */
export interface PaymentSummary {
  id?: string;
  /** The ID of the client that created this payment. */
  client_id?: string;
  external_id?: string;
  creation_mechanism?: PaymentCreationMechanism;
  created_at?: number;
  display_id?: string;
  state?: PaymentState;
  failure_reason?: PaymentFailureReason;
  /** Contains the reason that the attempt to refund this payment failed. */
  refund_failure_reason?: PaymentRefundFailureReason;
  orientation?: Orientation;
  amount?: Money;
  pull_amount?: Money;
  push_amount?: Money;
  rate_plan?: RatePlan;
  sender_alias?: Alias;
  recipient_alias?: Alias;
  sender_customer_token?: string;
  recipient_customer_token?: string;
  initiator_notes?: string;
}

/** An attempt to charge money from a card or withdraw from a bank account. */
export interface PaymentPull {
  id?: string;
  /** Token given to payment processor to identify this pull operation. */
  token?: string;
  /** Mechanism used to capture funds. */
  mechanism?: PaymentMoneyMovingMechanism;
  /**
   * Transaction ID returned by the money movement mechanism. If mechanism is Edje,
   * this is the Edje payment ID.
   */
  transaction_id?: string;
  /** The bank account used. If set, card and stored_value must be null. */
  bank_account?: BankAccount;
  /** The card used. If set, bank_account and stored_value must be null. */
  card?: PaymentCard;
  /** The stored value used. If set, bank_account and card must be null. */
  stored_value?: StoredValue;
  /** State of this transaction. */
  state?: PaymentPullState;
  /** Result from Octopus payment routing service */
  result?: OctopusResult;
  pull_failure_reason?: PaymentPullFailureReason;
  /** The refund performed on this payment pull. */
  refund_type?: PaymentPullRefundType;
  /** Details behind current pull state. */
  message?: string;
  /**
   * Authorization result. Represents a reason code that comes from an external processor. See:
   * https://git.sqcorp.co/projects/SQ/repos/java/browse/gateways/visa/src/main/resources/visa-common.yaml
   */
  authorize_result?: string;
  /** Gateway used. If using Edje, this is the gateway ID. */
  gateway?: string;
  authorized_at?: number;
  captured_at?: number;
  refunded_at?: number;
  /**
   * The time that this payment was charged back, or null if we have no record
   * of a chargeback. This message doesn't include chargeback details.
   */
  chargeback_at?: number;
  /** Returns the time that this payment's chargeback was reported as won. */
  chargeback_won_at?: number;
  /** Returns the time that this payment's chargeback was reported as lost. */
  chargeback_lost_at?: number;
  /** Gateway transaction id returned by Edje. Used to look up the pull externally. */
  gateway_transaction_id?: string;
  /** Unix time in millis */
  created_at?: number;
  /** Unix time in millis */
  updated_at?: number;
  statement_description?: string;
  movement_reason?: MovementReason;
  /**
   * 3DS authentication data. It should only be populated for consumption by Risk data pipelines.
   *
   * @deprecated
   */
  three_domain_secure_authentication?: ThreeDomainSecureAuthentication;
}

export enum PaymentPullFailureReason {
  /** FAILED - If used for ACH pulls, then corresponds to the Teller money transfer state FAILED. */
  FAILED = "FAILED",
  /** CANCELED - If used for ACH pulls, then corresponds to the Teller money transfer state CANCELLED. */
  CANCELED = "CANCELED",
}

/**
 * The type of refund done for a payment pull. States are stored as an enum in the refund_type
 * column in the payment_pulls table and the column will need to update to accommodate new states.
 */
export enum PaymentPullRefundType {
  /** REFUND_TO_INSTRUMENT - Refunded to originating instrument. */
  REFUND_TO_INSTRUMENT = "REFUND_TO_INSTRUMENT",
  /**
   * REFUND_TO_INSTRUMENT_WITHOUT_SUCCESSFUL_AUTH - Attempt to refund to instrument after a failed authorization. A previous Authorization
   * attempt crashed and we are unsure about the state.
   */
  REFUND_TO_INSTRUMENT_WITHOUT_SUCCESSFUL_AUTH = "REFUND_TO_INSTRUMENT_WITHOUT_SUCCESSFUL_AUTH",
  /** REFUND_TO_INSTRUMENT_WITHOUT_SUCCESSFUL_CAPTURE - In most cases, this voids the payment, except in the case of a TRANSPORT_ERROR. */
  REFUND_TO_INSTRUMENT_WITHOUT_SUCCESSFUL_CAPTURE = "REFUND_TO_INSTRUMENT_WITHOUT_SUCCESSFUL_CAPTURE",
  /** REFUND_TO_BALANCE - A refund was pushed to the customer's stored balance. */
  REFUND_TO_BALANCE = "REFUND_TO_BALANCE",
  /** CAPTURE_AND_THEN_REFUND_TO_BALANCE - Capture an authorized payment before attempting to refund to balance. */
  CAPTURE_AND_THEN_REFUND_TO_BALANCE = "CAPTURE_AND_THEN_REFUND_TO_BALANCE",
  /** CANCEL_ACH - The ACH pull has failed, cancel the pull to fail the payment. */
  CANCEL_ACH = "CANCEL_ACH",
  /** REFUND_TO_INSTRUMENT_FAILED_NO_SUCH_TRANSACTION - An attempt to refund to the originating instrument failed. */
  REFUND_TO_INSTRUMENT_FAILED_NO_SUCH_TRANSACTION = "REFUND_TO_INSTRUMENT_FAILED_NO_SUCH_TRANSACTION",
  /** SKIP - Refunding to sender was skipped in refunding step */
  SKIP = "SKIP",
}

/** An attempt to refund money to a card or deposit to a bank account. */
export interface PaymentPush {
  id?: string;
  /** Token given to payment processor to identify this pull operation. */
  token?: string;
  /** Mechanism used to push funds. */
  mechanism?: PaymentMoneyMovingMechanism;
  /**
   * Transaction ID returned by the money moving mechanism. If the mechanism is Edje,
   * this is the Edje payment ID.
   */
  transaction_id?: string;
  /** The bank account used. If set, card and stored_value must be null. */
  bank_account?: BankAccount;
  /** The card used. If set, bank_account and stored_value must be null. */
  card?: PaymentCard;
  /** The stored value used. If set, bank_account and card must be null. */
  stored_value?: StoredValue;
  /** Whether this transaction was successful. */
  state?: PaymentPushState;
  /** Result from Octopus payment routing service */
  result?: OctopusResult;
  push_failure_reason?: PaymentPushFailureReason;
  /**
   * Deprecated: This field will be replaced by new enum push_failure_reason.
   *
   * @deprecated
   */
  failure_reason?: string;
  /** Details behind current push state. */
  message?: string;
  paid_out_at?: number;
  reversed_at?: number;
  /** Gateway transaction id returned by Edje. Used to look up the unlinked refund externally. */
  gateway_transaction_id?: string;
  /** Unix time in millis */
  created_at?: number;
  /** Unix time in millis */
  updated_at?: number;
  statement_description?: string;
  movement_reason?: MovementReason;
}

export enum PaymentPushFailureReason {
  /** FAILED - If used for ACH pushes, then corresponds to the Teller money transfer state FAILED. */
  FAILED = "FAILED",
  /** CANCELED - If used for ACH pushes, then corresponds to the Teller money transfer state CANCELLED. */
  CANCELED = "CANCELED",
}

/**
 * The incoming email that created a Cash payment. It contains information about whether
 * the email came from the app, spoof results, headers, etc.
 */
export interface IncomingEmail {
  id?: string;
  headers?: Headers;
  body?: string;
  /** Risk/Compliance keywords found in the email body. */
  body_keywords?: string;
  spoof_decision?: SpoofDecision;
  /** True if payment was confirmed by the customer via email if the spoofchecker was UNSURE. */
  confirmed_by_email_token_link?: boolean;
  /**
   * True if payment was sent by app, and Cash relied on the verified app token to short circuit
   * a confirmation email if the spoofchecker was UNSURE.
   */
  confirmed_by_app_token?: boolean;
  /**
   * Unless deleted by the customer, emails sent from the v1 App contain a payment token.
   * This can be passed around and stored freely since, once seen by Franklin, it cannot be re-used.
   */
  contains_app_payment_token?: boolean;
  /** Unix time in millis */
  created_at?: number;
  /** Unix time in millis */
  updated_at?: number;
}

export interface SpoofDecision {
  result?: SpoofDecisionResult;
  reason?: SpoofDecisionReason;
  processed_with_default_policy?: boolean;
}

export enum SpoofDecisionResult {
  NOT_SPOOFED = "NOT_SPOOFED",
  SPOOFED = "SPOOFED",
  UNSURE = "UNSURE",
}

export enum SpoofDecisionReason {
  ALL_REQUIRED_PASS = "ALL_REQUIRED_PASS",
  SOME_REQUIRED_DO_NOT_PASS = "SOME_REQUIRED_DO_NOT_PASS",
  NONE_REQUIRED_PASS = "NONE_REQUIRED_PASS",
  SOFT_CHECKS_PASSED = "SOFT_CHECKS_PASSED",
  FAILED_TO_SPOOFCHECK = "FAILED_TO_SPOOFCHECK",
  FORWARDED_SQUAREUP = "FORWARDED_SQUAREUP",
  MISMATCHING_SENDER = "MISMATCHING_SENDER",
  MULTIPLE_FROM_HEADERS = "MULTIPLE_FROM_HEADERS",
  NO_POLICY_FOR_DOMAIN = "NO_POLICY_FOR_DOMAIN",
  INCORRECT_BOUNDARY_TOKEN = "INCORRECT_BOUNDARY_TOKEN",
  FOUND_SENDER_HEADER = "FOUND_SENDER_HEADER",
}
